import { decodePeriodValue } from '@components'

import { maxDateDefault, minDateDefault } from '@billy/design-system'
import { endOfMonth, endOfQuarter, endOfYear, format, startOfMonth, startOfQuarter, startOfYear } from 'date-fns'
import { parse } from 'qs'

import { PeriodMode } from '../../../enums/periodMode'
import type { PeriodValue } from '../../../types/periodValue'
import { formatDate } from '../../../utils/date-fns'
import type { ReviewEntriesMonthDateRange } from '../types/reviewEntriesMonthPeriod'

const DATE_FORMAT = 'yyyy-MM-dd'
const LEGACY_MONTH_PERIOD_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/

export const getCurrentReviewEntriesPeriodValue = (): PeriodValue => {
  const now = new Date()

  return {
    mode: PeriodMode.Month,
    value: now.getMonth(),
    year: now.getFullYear(),
  }
}

export const getReviewEntriesPeriodFromUrl = (): string => {
  const search = window.location.href.split('?')[1] ?? ''
  const parsed = parse(search, { ignoreQueryPrefix: true, allowDots: true })

  return typeof parsed.period === 'string' ? parsed.period : ''
}

const decodeLegacyMonthPeriod = (periodValue: string): PeriodValue | undefined => {
  const match = LEGACY_MONTH_PERIOD_PATTERN.exec(periodValue)

  if (!match) {
    return undefined
  }

  const year = Number(match[1])
  const month = Number(match[2]) - 1

  return {
    mode: PeriodMode.Month,
    value: month,
    year,
  }
}

export const resolveReviewEntriesPeriodValue = (encodedPeriod?: string): PeriodValue => {
  if (!encodedPeriod) {
    return getCurrentReviewEntriesPeriodValue()
  }

  if (!encodedPeriod.includes(':')) {
    if (encodedPeriod === PeriodMode.All) {
      return { mode: PeriodMode.All }
    }

    return decodeLegacyMonthPeriod(encodedPeriod) ?? getCurrentReviewEntriesPeriodValue()
  }

  const decodedPeriod = decodePeriodValue(encodedPeriod)

  if (!decodedPeriod || (decodedPeriod.mode === PeriodMode.All && encodedPeriod !== PeriodMode.All)) {
    return getCurrentReviewEntriesPeriodValue()
  }

  return decodedPeriod
}

export const getReviewEntriesDateRangeFromPeriodValue = (periodValue: PeriodValue): ReviewEntriesMonthDateRange => {
  const { customDate, mode, value, year } = periodValue

  if (customDate) {
    return {
      beginDate: customDate.startDate
        ? format(customDate.startDate, DATE_FORMAT)
        : (formatDate(minDateDefault, DATE_FORMAT) as string),
      endDate: customDate.endDate
        ? format(customDate.endDate, DATE_FORMAT)
        : (formatDate(maxDateDefault, DATE_FORMAT) as string),
    }
  }

  switch (mode) {
    case PeriodMode.Month: {
      const selectedDate = new Date(year as number, value as number)

      return {
        beginDate: formatDate(startOfMonth(selectedDate), DATE_FORMAT) as string,
        endDate: formatDate(endOfMonth(selectedDate), DATE_FORMAT) as string,
      }
    }

    case PeriodMode.Quarter: {
      const quarterStart = startOfQuarter(new Date(year as number, (value as number) * 3, 1))
      const quarterEnd = endOfQuarter(quarterStart)

      return {
        beginDate: formatDate(quarterStart, DATE_FORMAT) as string,
        endDate: formatDate(quarterEnd, DATE_FORMAT) as string,
      }
    }

    case PeriodMode.FiscalYear: {
      const fiscalYear = value ?? year

      return {
        beginDate: formatDate(startOfYear(new Date(fiscalYear as number, 0, 1)), DATE_FORMAT) as string,
        endDate: formatDate(endOfYear(new Date(fiscalYear as number, 0, 1)), DATE_FORMAT) as string,
      }
    }

    case PeriodMode.All:
      return {
        beginDate: formatDate(minDateDefault, DATE_FORMAT) as string,
        endDate: formatDate(maxDateDefault, DATE_FORMAT) as string,
      }

    default:
      return getReviewEntriesDateRangeFromPeriodValue(getCurrentReviewEntriesPeriodValue())
  }
}

export const getReviewEntriesDateRangeFromEncodedPeriod = (encodedPeriod?: string): ReviewEntriesMonthDateRange =>
  getReviewEntriesDateRangeFromPeriodValue(resolveReviewEntriesPeriodValue(encodedPeriod))
